// Copyright ©️ 2026 eVolve MEP, LLC

import React, { cloneElement, isValidElement, useState, useMemo, useCallback, useEffect, useRef } from 'react';

import { Tooltip, type TooltipProps } from '@mantine/core';
import { useTimeout } from '@mantine/hooks';

import { isNil } from 'helpers/isNotNil';
import { useIsMobile } from 'hooks/useIsMobile';
import { DEFAULT_TRANSITIONS_DURATION } from 'styles/theme/useGenerateMantineTheme';

const shadow: Partial<React.CSSProperties> = {
  borderWidth: 1,
  borderColor: 'lightgray',
  borderStyle: 'solid',
  boxShadow: '0 0 8px 0 rgba(0, 0, 0, 0.2)',
};

/** How long a tap-opened tooltip stays visible on mobile before closing itself, in ms. */
const MOBILE_AUTO_CLOSE_MS = 10_000;

/**
 * A tooltip intended to mimic the styling of built-in AG Grid tooltips.
 *
 * @remarks On mobile, the tooltip is tap-driven instead of hover-driven.
 */
export const AgGridStyleTooltip = ({
  style,
  styles,
  transitionProps,
  position,
  openDelay = 700,
  opened,
  children,
  openOnTap,
  ...props
}: TooltipProps & {
  /**
   * If `true`, will open the tooltip on tap on mobile.
   * @defaultValue false
   */
  openOnTap?: boolean;
}) => {
  const isMobile = useIsMobile();

  const [tapOpened, setTapOpened] = useState(false);
  const { start: startAutoClose, clear: clearAutoClose } = useTimeout(() => setTapOpened(false), MOBILE_AUTO_CLOSE_MS);
  // Only take over when the caller isn't controlling `opened` themselves.
  const tapToOpen = isMobile && isNil(opened);
  const closeTapOpened = useCallback(() => {
    clearAutoClose();
    setTapOpened(false);
  }, [clearAutoClose]);
  const toggleTapOpened = useCallback(() => {
    if (tapOpened) {
      closeTapOpened();
    } else {
      startAutoClose();
      setTapOpened(true);
    }
  }, [closeTapOpened, startAutoClose, tapOpened]);

  // The tooltip's target element, captured lazily from the tap's pointerdown — refs can't be
  // threaded through arbitrary `children`. Used to tell taps on the target (which toggle) apart
  // from taps outside (which dismiss).
  const targetRef = useRef<HTMLElement | null>(null);
  // Dismiss on any tap outside the target. Attached only while the tooltip is tap-opened, so a
  // page full of tooltips (e.g. a grid) doesn't accumulate a window listener per instance.
  useEffect(() => {
    if (!tapOpened) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !targetRef.current?.contains(event.target)) {
        closeTapOpened();
      }
    };
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [tapOpened, closeTapOpened]);

  // Toggling on pointerdown (not onClick) keeps the only requirement on `children` the one
  // Mantine's Tooltip already imposes for hover — passing unknown props through to the DOM node.
  // Intercepting onClick would additionally depend on each wrapper's own onClick contract
  // (e.g. BadgeButton invokes its onClick prop without the event).
  const target = useMemo(
    () =>
      tapToOpen && isValidElement<{ onPointerDown?: React.PointerEventHandler<HTMLElement> }>(children)
        ? cloneElement(children, {
            onPointerDown: (event) => {
              children.props.onPointerDown?.(event);
              if (openOnTap) {
                if (event.currentTarget instanceof HTMLElement) {
                  targetRef.current = event.currentTarget;
                }
                toggleTapOpened();
              }
            },
          })
        : children,
    [children, openOnTap, tapToOpen, toggleTapOpened],
  );

  return (
    <Tooltip
      color="white"
      c="gray.9"
      multiline
      data-multiline
      arrowSize={8}
      withinPortal
      openDelay={isMobile ? undefined : openDelay}
      opened={tapToOpen ? tapOpened : opened}
      mah="calc(100vh - 10rem)"
      maw={380}
      ta="center"
      floatingStrategy="fixed"
      middlewares={{
        shift: {
          crossAxis: !!(position?.startsWith('left') || position?.startsWith('right')),
        },
      }}
      {...props}
      transitionProps={{ transition: 'fade', duration: DEFAULT_TRANSITIONS_DURATION, ...transitionProps }}
      style={{
        ...shadow,
        // TODO: `.style` can now be a function, need to handle it
        // oxlint-disable-next-line typescript/no-misused-spread
        ...style,
      }}
      styles={{
        tooltip: { overflowWrap: 'break-word' },
        arrow: {
          ...shadow,
          boxShadow: '0 0 8px -8px rgba(0, 0, 0, 0.2)',
        },
        // TODO: `.style` can now be a function, need to handle it
        // oxlint-disable-next-line typescript/no-misused-spread
        ...styles,
      }}
    >
      {target}
    </Tooltip>
  );
};
